// Typed wrappers over the PMS broker-facing account API, consuming the
// brokerage-owned account contract shipped in PMS-S107 + the broker reference
// endpoints in PMS-S066. An account is owned by exactly ONE brokerage: the
// broker who works it fully owns it — create AND edit — and only ever sees its
// own brokerage's accounts (own-scoped from the session; there is no global
// directory, no cross-brokerage overlay, and no de-duplication). SSN is never
// returned; EIN is included only on the single detail fetch. All calls go
// through the AuthedRequester so they inherit the access-token injection +
// silent-refresh-on-401 (BRK-S015). See docs/accounts.md.

import type { AuthedBlobRequester, AuthedRequester } from "@/lib/brokerAuth";
import { ApiError, type FileResponse } from "@/lib/api";
import {
  hasPrimaryPhone,
  type PhoneDetail,
  type PhoneInput,
} from "@/lib/phone";
import type { PersonnelRole } from "@/lib/personnelRoles";
// The program vocabulary is the BOND seam's (it is published on both blocks and
// is one closed list). Imported rather than restated, unlike the terms — those
// are two independent PMS surfaces that happen to agree, where this is one
// vocabulary PMS derives in one place.
import type { BondProgramType } from "@/lib/brokerBonds";

// The phone contract lives in @/lib/phone alongside the normalize/format helpers
// (phones hang off accounts AND personnel, so neither owns them); re-exported
// here so account consumers import one module.
export type { PhoneDetail, PhoneInput, PhoneLineType } from "@/lib/phone";
// Likewise the role vocabulary — it hangs off personnel, and @/lib/personnelRoles
// owns the labels/helpers that both the form and the detail read.
export type { PersonnelRole } from "@/lib/personnelRoles";

const BASE = "/api/broker/accounts";

// ─── Account record (brokerage-owned, broker-editable) ────────────────────────

export type EntityType =
  | "sole_proprietorship"
  | "partnership"
  | "llc"
  | "llp"
  | "c_corporation"
  | "s_corporation"
  | "trust"
  | "joint_venture"
  | "not_for_profit"
  | "unincorporated_association";

/** Account fields in a list row — summary only; never carries ssn/ein. */
export interface AccountSummary {
  id: string;
  accountNumber: string;
  legalName: string;
  dbaName: string;
  entityType: EntityType | null;
  emailAddress: string;
  /** The business's numbers, PRIMARY FIRST (PMS-S173 replaced the single
   *  `primaryPhone` string with this array). Empty on a draft. */
  phones: PhoneDetail[];
}

export interface Address {
  id: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  formattedAddress: string | null;
  /** Served on every read; this portal has no input for them, but a save
   *  rewrites the Location wholesale, so AccountForm carries them back out
   *  (BRK-S223). Optional here only so older fixtures still type-check. */
  addressLine2?: string | null;
  county?: string | null;
}

export type MaritalStatus =
  | "single"
  | "married"
  | "domestic_partnership"
  | "separated"
  | "divorced"
  | "widowed";

/** One of a person's own addresses, as the account read serves it. */
export interface PersonnelAddress {
  type: "home" | "mailing" | "previous" | "other";
  isPrimary: boolean;
  street: string;
  addressLine2: string | null;
  city: string;
  state: string;
  zip: string;
  county: string | null;
}

/**
 * What a caller that may NOT read the plaintext can still be told about a
 * person's write-only PII (PMS-S237, decision D-11): that a value EXISTS, and —
 * for the SSN only — its last four digits.
 *
 * Served from ciphertext PRESENCE plus a stored plaintext `ssn_last4` column,
 * so no read path decrypts to produce it, and last four alone identifies
 * nobody. `last4` is **null** on a row encrypted before that column existed: on
 * file, last four unknown. There is no `last4` for the driver's licence at all
 * — PMS stores none — so this portal must never render digits for it.
 *
 * REQUIRED on every personnel entry in the contract. It is the only thing this
 * portal ever learns about a stored SSN: the plaintext is write-only and never
 * appears in a broker-facing response.
 */
export interface PersonnelPii {
  ssn: { onFile: boolean; last4: string | null };
  driversLicense: { onFile: boolean };
  /**
   * Whether a server-side write assembling a vendor payload would read THIS
   * person's stored SSN / driver's licence when the caller leaves the field
   * blank (PMS-S238).
   *
   * True for at MOST one person on an account, and for NOBODY when no personnel
   * holds an ownership share — the same fact a bond step save reports as
   * `NO_OWNER_ON_ACCOUNT`. Never a cue to fall back to the first entry.
   *
   * Read this INSTEAD of ranking `ownershipPercentage`: two 50/50 owners have no
   * largest shareholder, so the answer rests entirely on a tie-break, and a
   * consumer that re-derives it will eventually promise a value the server would
   * never send. BRK re-derived it until BRK-S110.
   *
   * Independent of `onFile` — the person a write reads from is the same before
   * they have supplied anything.
   */
  usedForWrite: boolean;
}

export interface Personnel {
  id: string;
  firstName: string;
  lastName: string;
  title: string | null;
  email: string | null;
  /** A person's numbers, primary first (PMS-S173 replaced `phone`). */
  phones: PhoneDetail[];
  ownershipPercentage: string | null;
  /** What this person DOES for the account (PMS-S178) — distinct from `title`,
   *  their job title. Replaces the account's `contacts[]`: a person can hold
   *  several roles without being entered twice. */
  roles: PersonnelRole[];
  /** The write-only-PII indicator (PMS-S237). See `PersonnelPii`. */
  pii: PersonnelPii;
  // Served on every read but never edited here. A PUT replaces each person
  // wholesale, so AccountForm carries these back out untouched or the save
  // nulls them (BRK-S223). Optional only so older fixtures still type-check.
  duties?: string | null;
  birthDate?: string | null;
  wcInclusion?: "included" | "excluded" | null;
  /** A decimal string on the read, a number on the write. */
  annualRemuneration?: string | null;
  maritalStatus?: MaritalStatus | null;
  driversLicenseState?: string | null;
  addresses?: PersonnelAddress[];
  /** The `personnel[].id` of this person's spouse on this account. */
  spouseId?: string | null;
}

/**
 * A NAICS classification — the insured's **General Trade** (BRK-S138).
 *
 * The DESCRIPTION rides with the code, resolved server-side, so the profile can
 * name the trade without a second lookup and without this portal shipping a
 * copy of the NAICS table. A code with no matching row comes back with the
 * `naicsCode` scalar set and this object **null** — which is a real state (the
 * code was captured before the table had it), rendered as the bare code rather
 * than as nothing.
 */
export interface NaicsCode {
  code: string;
  description: string;
}

/**
 * WHO the insured's principal owner is, as the account read states it
 * (BRK-S138).
 *
 * Resolved SERVER-SIDE from the personnel holding an ownership share, and read
 * rather than re-derived. PMS-S238's lesson, in the `usedForWrite` note on
 * `PersonnelPii` above, is the whole reason: two 50/50 owners have no largest
 * shareholder, so the answer rests entirely on a tie-break, and a consumer that
 * ranks `ownershipPercentage` itself will eventually name somebody the server
 * would not. BRK re-derived exactly this until BRK-S110.
 *
 * **null** when nobody holds a share — the same fact a bond step save reports as
 * `NO_OWNER_ON_ACCOUNT`, and never a cue to fall back to the first person on the
 * roster.
 *
 * `name` is a whole display name, not the split fields: this is a line on a
 * profile, and the split lives on `Personnel` for the form that edits it.
 */
export interface PrincipalOwner {
  name: string;
  /**
   * 0–100, as a **NUMBER**.
   *
   * Deliberately unlike `Personnel.ownershipPercentage`, which is a string
   * because the pg driver round-trips an exact decimal as text (PMS-S006). This
   * one is a derived figure PMS computes and publishes as a number
   * (reconciled against the as-built contract, PMS-S314), so it arrives already
   * coerced and this portal does not decide the precision either way.
   */
  ownershipPercentage: number;
}

/**
 * Where the insured stands with Signal's standing surety PROGRAM (BRK-S138 /
 * BRK-S139, consuming the account side of PMS-S303).
 *
 * The ACCOUNT's block, and deliberately not the bond order's
 * (`BondPreapproval`): the two carry the same terms and DIFFERENT status
 * vocabularies, because they answer different questions. An order asks "what
 * happened on THIS order", so its `pending` is a real state and a real wait. An
 * account asks "does this insured hold a program", which is settled by
 * definition — so `none` replaces `pending`, and there is nothing to poll.
 *
 * Mirroring them as one type would mean one of the two unions carrying a member
 * its own surface can never receive, and a consumer writing copy for it.
 */
export type AccountPreapprovalStatus = "approved" | "not_granted" | "none";

/**
 * The program terms a granted preapproval carries — and the whole of them.
 *
 * PARITY WITH THE ORDER VIEW'S `BondPreapprovalTerms`, deliberately restated
 * rather than imported: the two are the same three figures on two independent
 * PMS surfaces, and `brokerAccounts` importing from `brokerBonds` would tie the
 * account seam's shape to the bond seam's. `bondRatePercent` is still the ONE
 * conversion for the rate (it is per $1,000, not a percent — rendering it as one
 * overstates the rate by 10×), because that is a display rule and not a shape.
 */
export interface AccountPreapprovalTerms {
  singleBondLimit: number;
  aggregateProgramLimit: number;
  ratePer1000: number;
}

/**
 * The account read's preapproval block.
 *
 * `terms` and `evaluatedAt` are **null** for anything but `approved` — there are
 * no terms on a program that was not granted, and a zero limit would be a false
 * claim rather than an absent one.
 *
 * `not_granted` IS NOT A DECLINE, and nothing here explains the decision: no
 * score, no reason, no surety name (PMS-E015). `not_granted` covers a refusal
 * AND a withdrawal indistinguishably, on purpose. `none` is the third fact — the
 * insured has never been evaluated — and it reads differently: there is
 * something for the broker to DO about it (start a bond request), where a
 * refusal is an answer they already have.
 */
export interface AccountPreapproval {
  status: AccountPreapprovalStatus;
  /**
   * WHICH standing program the insured holds (PMS-S435, adopted in BRK-S161).
   *
   * **Signal Express** is granted off the bureau pulls alone; **Signal
   * Advance** is the program their connected bank and accounting feeds
   * support. `null` whenever none is in force — `not_granted` and `none` — and
   * never defaulted: naming a program beside a status that says the insured
   * holds nothing would name a program they do not have.
   *
   * It is what decides whether the account block offers "Opt in to Signal
   * Advance": only `express` has somewhere to go.
   *
   * ⚠️ `advance`, never `advanced`.
   */
  programType: BondProgramType | null;
  terms: AccountPreapprovalTerms | null;
  evaluatedAt: string | null;
  /** Whether the Program Indication LETTER can be fetched. Its own axis, not
   *  derivable from `status`: minting is a later step than the decision, so an
   *  approved account can carry no PDF for a moment (BRK-S133's lesson). */
  indicationAvailable: boolean;
  /**
   * When the standing program runs out — one year from the decision, DERIVED
   * upstream rather than stored (adopted at the wave-1 re-pin, BRK-S179).
   *
   * **Null whenever no program is in force, a LAPSED one included.** An expired
   * program reads exactly like a refusal on this surface — the same rule a
   * withdrawal follows — so there is never a past date here and never a way to
   * tell the two apart. A non-null value is always in the future, which is what
   * makes it safe to render without comparing it to a clock.
   */
  expiresAt: string | null;
}

/** The account on the detail fetch — EIN included, SSN never present. Carries the
 *  industry/CMS ids so the edit form can pre-select them (the broker owns and
 *  edits the record). */
export interface AccountRecord extends AccountSummary {
  ein: string | null;
  /**
   * The insured's website — canonicalised on write (scheme optional in,
   * `https://` out).
   *
   * ⚠️ THIS **REPLACED** `url` ON THE BROKER CONTRACT (PMS-S314, reconciled
   * against the as-built spec): "the admin surface's `url` field under the
   * broker contract's name — the same stored value, not a second one." So it is
   * a RENAME rather than an addition, which makes it breaking in both
   * directions, and the consumer adopts first — the read stays tolerant of the
   * old spelling (`accountWebsite`) and the WRITE sends only this one. Read
   * both, write one: the same fold this repo already applies to the accounting
   * link's `linked`/`connected`.
   */
  website: string | null;
  /**
   * The PRE-PMS-S314 spelling of the same fact.
   *
   * OPTIONAL, because the contract no longer publishes it at all — declaring it
   * required would be this portal asserting a field PMS has stopped sending.
   * It exists only so a read from a PMS that predates the rename still renders,
   * and it closes on its own the moment that deploy lands. Never written.
   */
  url?: string | null;
  /** The insured's General Trade, as the 6-digit NAICS code alone. The scalar
   *  the WRITE carries; `naics` is the resolved pair the profile reads. */
  naicsCode: string | null;
  /** The same code with its description resolved server-side — null when the
   *  code names no row (see `NaicsCode`). */
  naics: NaicsCode | null;
  /** The principal owner, resolved server-side — never re-derived here. */
  principalOwner: PrincipalOwner | null;
  /**
   * Where the insured stands with the standing surety program (BRK-S139).
   *
   * REQUIRED UPSTREAM since PMS-S315, and OPTIONAL here — which is now about
   * the DEPLOY and nothing else. The pin sits on that branch, so the shape
   * itself is guarded (`test/lib/suretyProgramContract.test.ts`); what is not
   * yet true is that the PMS this portal talks to has shipped it. Declaring it
   * required would make the profile white-screen against today's live server
   * for a key the spec promises tomorrow.
   *
   * An absent block reads as `none` at the one place that renders it, which is
   * also the honest reading rather than a placeholder: a PMS that says nothing
   * about a program has not said the insured holds one. The window closes when
   * PMS-S315 deploys, and the fallback becomes unreachable rather than wrong.
   */
  preapproval?: AccountPreapproval;
  contractorLicenseNumber: string | null;
  industryId: string | null;
  constructionManagementSystemId: string | null;
  legalAddress: Address | null;
  mailingAddress: Address | null;
  physicalLocations: Address[];
  personnel: Personnel[];
  // Served on every read; typed here for the account record page. Optional
  // only so older fixtures still type-check.
  status?: "draft" | "active";
  /** Who created the record: an admin in PMS or a broker in this portal. */
  source?: "admin" | "broker";
  createdAt?: string;
  updatedAt?: string;
  yearsInBusiness?: number | null;
  /** A decimal string on the read, a number on the write. */
  annualRevenues?: string | null;
  numEmployees?: number | null;
  natureOfBusiness?: string | null;
}

/**
 * The insured's website, whichever spelling the server used.
 *
 * ONE PLACE, so no surface has to know there are two. `website` is the
 * contract's (PMS-S314, which RENAMED it); `url` is what every account read
 * carried before that, and a read from a PMS which predates the deploy still has
 * only that. Both blank reads as **absent**, never as an empty link — a stored
 * `" "` would otherwise render an anchor to nowhere.
 */
export function accountWebsite(account: {
  website?: string | null;
  url?: string | null;
}): string | null {
  const value = account.website?.trim() || account.url?.trim() || "";
  return value === "" ? null : value;
}

/** The brokerage that owns the account, embedded on the detail (own-scoped, so
 *  always the caller's own brokerage — surfaced to confirm ownership). */
export interface AccountBrokerage {
  id: string;
  name: string;
}

// The account/{id} detail (and create/edit responses) return the account FLAT —
// its own fields at the top level with the brokerage, servicing broker, and
// notes embedded alongside — NOT wrapped under an `account` key.

/** The account's servicing broker (non-sensitive subset, PMS-S066). */
export interface ServicingBroker {
  id: string;
  firstName: string;
  lastName: string;
  businessUnit: string | null;
  email: string;
}

/** A note on the account, author-attributed server-side (PMS-S107). */
export interface AccountNote {
  id: string;
  accountId: string;
  authorBrokerId: string | null;
  body: string;
  createdAt: string;
  updatedAt: string;
}

// PMS-S178 merged the account's `contacts` into `personnel` + a role join, so
// there is no AccountContact type, no `contacts[]` on the detail, and no
// contacts endpoints (they 404 now). A contact is a person with a role — see
// `roles` on Personnel/PersonnelInput and @/lib/personnelRoles.

// ─── Responses ────────────────────────────────────────────────────────────────

/** One page of the brokerage's own accounts (plain summaries). */
export interface AccountsPage {
  items: AccountSummary[];
  total: number;
  limit: number;
  offset: number;
}

/** A full account view: the flat account record with its owning brokerage,
 *  servicing broker, and notes embedded. */
export interface AccountDetail extends AccountRecord {
  brokerage: AccountBrokerage | null;
  servicingBroker: ServicingBroker | null;
  notes: AccountNote[];
}

// ─── Request bodies ─────────────────────────────────────────────────────────-

export interface AddressInput {
  street: string;
  city: string;
  state: string;
  zip: string;
  addressLine2?: string;
  county?: string;
}

export interface PersonnelAddressInput extends AddressInput {
  type: PersonnelAddress["type"];
  isPrimary: boolean;
}

export interface PersonnelInput {
  /**
   * The `id` this person was READ with — echo it so the server knows which
   * stored person this entry is (PMS-S191). A PUT replaces the personnel set
   * wholesale, so without an id the server can only match on name, and two
   * people sharing one name are indistinguishable: that write used to null both
   * of their encrypted PII and is now refused outright (PMS-S201).
   *
   * Optional because a person the broker just ADDED genuinely has none — never
   * fabricate or reuse one. Send it for everyone else, always: SSN and drivers
   * licence are write-only, so a broker read can never show which people hold
   * the PII that makes identity matter (BRK-S095).
   */
  id?: string;
  firstName: string;
  lastName: string;
  title?: string;
  email?: string;
  /** Omitted means "leave the person's numbers untouched"; an empty array
   *  clears them (PMS-S173). */
  phones?: PhoneInput[];
  /** The person's roles (PMS-S178), written through the account POST/PUT — there
   *  is no separate roles endpoint. A duplicated role in one entry is a 400, so
   *  send each at most once (see normalizeRoles). */
  roles?: PersonnelRole[];
  /** The person's ownership share (0–100). This portal has no input for it, but a
   *  PUT replaces the nested personnel set WHOLESALE, so an edit must send back
   *  the share it loaded or the server drops it — and PMS keys the LLC
   *  member/manager count and the credit-bureau pull on this field, not on the
   *  `owner` role. */
  ownershipPercentage?: number;
  // Carried back out untouched for the same reason (BRK-S223): the wholesale
  // replace nulls anything a loaded person's entry omits. Never ssn or
  // driversLicenseNumber, which are write-only and absent means "keep".
  duties?: string;
  birthDate?: string;
  wcInclusion?: "included" | "excluded";
  annualRemuneration?: number;
  maritalStatus?: MaritalStatus;
  driversLicenseState?: string;
  addresses?: PersonnelAddressInput[];
  /** Only ever a person in the SAME write, by the id it was read with; PMS
   *  400s a link to anyone else. */
  spouse?: { personnelId: string };
}

/** Create/edit input. The server forces the owning brokerage from the session,
 *  so it is never part of the body. Only `legalName` is always sent; every other
 *  field is OMITTED when the broker left it blank (BRK-S057 partial PUT) — never
 *  sent as an empty string (the server 400s those), and a partial PUT leaves
 *  omitted fields unchanged. `brokerId` is the servicing broker (a broker in the
 *  caller's own brokerage). */
export interface AccountInput {
  legalName: string;
  yearsInBusiness?: number;
  annualRevenues?: number;
  numEmployees?: number;
  natureOfBusiness?: string;
  dbaName?: string;
  emailAddress?: string;
  /** At least one number with one marked primary for an `active` account; a
   *  partial PUT may omit it entirely to leave the set untouched (PMS-S173). */
  phones?: PhoneInput[];
  /** Optional — the PMS industry_id column is nullable (industryId .optional(),
   *  relaxed under PMS-S120), so it is omitted from the body when unset. */
  industryId?: string;
  legalAddress?: AddressInput;
  mailingAddress?: AddressInput;
  personnel?: PersonnelInput[];
  ein?: string;
  entityType?: EntityType;
  constructionManagementSystemId?: string;
  /** `minLength: 1` upstream, so it is omitted rather than sent blank. */
  contractorLicenseNumber?: string;
  /**
   * The insured's website. `website` is what the contract ACCEPTS (PMS-S314);
   * the read tolerates the older `url` spelling, the write does not send it —
   * see `accountWebsite`. Read both, write one.
   *
   * `minLength: 1` upstream, so a cleared field is OMITTED rather than sent as
   * `""` — see the note on this interface's header.
   */
  website?: string;
  /** The insured's General Trade, as the NAICS code alone — SIX DIGITS
   *  (`^\d{6}$` upstream). The write carries the CODE and never the
   *  description: the description is the server's to resolve, and accepting one
   *  would be this portal proposing a classification. */
  naicsCode?: string;
  brokerId?: string;
}

// ─── Required account floor (BRK-S046) ────────────────────────────────────────

/** The minimal fields a subset of any account shape exposes for the floor check.
 *  Both the create/edit input and a fetched detail satisfy this. Every field is
 *  optional so a PARTIAL account input (BRK-S057) also satisfies it — the checks
 *  below treat absent and blank identically. */
export interface AccountFloorFields {
  legalName?: string;
  emailAddress?: string;
  /** Loose on purpose so BOTH a fetched account (`PhoneDetail[]`, where every
   *  field is populated) and a partial write (`PhoneInput[]`) satisfy it — the
   *  floor only asks how many numbers there are and which is primary. */
  phones?: { number?: string; isPrimary?: boolean }[];
  legalAddress?: {
    street: string;
    city: string;
    state: string;
    zip: string;
  } | null;
  /** Accepted but not part of the floor — a completed account may store it null
   *  (same-as-legal), so it must never read as incomplete. */
  mailingAddress?: {
    street: string;
    city: string;
    state: string;
    zip: string;
  } | null;
  personnel?: unknown[];
  /** The entity type (account-scope; a carrier may require it at quote stage —
   *  PIE reads it for the legal-entity classification, PMS-S149). */
  entityType?: string | null;
}

/**
 * The authoritative required floor a draft insured must meet before it can be
 * quoted (PMS `PUT /accounts/{id}` required set): legal name, email, at least
 * one phone number with one marked primary, a full legal address, and at least
 * one person. DBA + industry are NOT part of
 * the floor (both optional). Returns a handled message for the first missing
 * field, or null when the floor is met. Mailing address is intentionally not
 * checked — a completed account may store it null (same-as-legal).
 */
export function accountFloorError(f: AccountFloorFields): string | null {
  if (!f.legalName?.trim()) return "Enter the insured’s legal name.";
  if (!f.emailAddress?.trim()) return "Enter an email address.";
  // PMS-S173: the phone floor is the ARRAY — at least one number, exactly one
  // primary (the PMS `active`-account rule). Separate messages, because "you
  // typed no number" and "you typed two and marked neither" are different fixes.
  if (!f.phones?.some((p) => p.number === undefined || p.number.trim()))
    return "Add at least one phone number.";
  if (!hasPrimaryPhone(f.phones))
    return "Mark one phone number as the primary.";
  const a = f.legalAddress;
  if (
    !a?.street?.trim() ||
    !a?.city?.trim() ||
    !a?.state?.trim() ||
    !a?.zip?.trim()
  )
    return "Complete the legal address (street, city, state, and ZIP).";
  if (!f.personnel || f.personnel.length < 1)
    return "Add at least one person (first and last name).";
  return null;
}

/** Boolean the standalone /accounts create+edit pages read. The submission flow
 *  no longer uses this hardcoded floor — it derives a STAGE-AWARE floor from the
 *  product manifest instead (see accountMeetsFloor, BRK-S057). */
export function isAccountComplete(f: AccountFloorFields): boolean {
  return accountFloorError(f) === null;
}

// ─── Stage-aware account floor (BRK-S057) ─────────────────────────────────────

/** A manifest account-scope floor field (the subset accountMeetsFloor reads). */
export interface AccountFloorField {
  key: string;
  label: string;
}

function hasCompleteAddress(
  a:
    | { street: string; city: string; state: string; zip: string }
    | null
    | undefined,
): boolean {
  return Boolean(
    a?.street?.trim() && a?.city?.trim() && a?.state?.trim() && a?.zip?.trim(),
  );
}

// Whether an account satisfies a single manifest account-scope floor field,
// keyed by the manifest field key. Keys we can't map return `true` (fail open):
// the server stays the authority and will name anything truly missing in its
// structured 400 (PMS-S144), so a client we-can't-check never blocks a
// legitimate submission (NN #7).
function accountFieldSatisfied(key: string, f: AccountFloorFields): boolean {
  switch (key) {
    case "legalName":
      return Boolean(f.legalName?.trim());
    case "emailAddress":
    case "email":
      return Boolean(f.emailAddress?.trim());
    // `primaryPhone`/`phone` are the pre-PMS-S173 spellings, kept so an older
    // manifest key still maps onto the array.
    case "phones":
    case "primaryPhone":
    case "phone":
      return hasPrimaryPhone(f.phones);
    case "mailingAddress":
      // The server resolves the rated location from the mailing address, which
      // defaults to the legal address (mailing-same) — so either satisfies it.
      return (
        hasCompleteAddress(f.mailingAddress) ||
        hasCompleteAddress(f.legalAddress)
      );
    case "legalAddress":
      return hasCompleteAddress(f.legalAddress);
    case "personnel":
      return (f.personnel?.length ?? 0) >= 1;
    case "entityType":
      return Boolean(f.entityType);
    default:
      return true;
  }
}

/**
 * The manifest account-scope floor fields an account does NOT yet satisfy
 * (BRK-S057), structured for a per-field checklist (the BRK-S052 missing-fields
 * pattern). Pass the fields from `accountFloorFields(manifest, stage)`.
 */
export function unmetAccountFields(
  fields: AccountFloorField[],
  account: AccountFloorFields,
): AccountFloorField[] {
  return fields.filter((field) => !accountFieldSatisfied(field.key, account));
}

/** Whether an account meets a manifest account-scope floor (all fields satisfied). */
export function accountMeetsFloor(
  fields: AccountFloorField[],
  account: AccountFloorFields,
): boolean {
  return unmetAccountFields(fields, account).length === 0;
}

// ─── The standing program's letter and the account's documents (BRK-S139) ────

/**
 * GET /api/broker/accounts/{id}/indication.pdf — the Program Indication
 * letter's BYTES for the ACCOUNT's standing program.
 *
 * The account-level sibling of the order-level route BRK-S133 consumes, and the
 * SAME reason it takes the blob requester rather than the JSON one: `apiRequest`
 * would `JSON.parse` a PDF, and a plain `<a href>` or `<iframe src>` cannot
 * fetch it at all — the route is bearer-authenticated and neither carries an
 * `Authorization` header. So the SPA fetches the bytes and hands the browser a
 * blob it can save.
 *
 * Offer it only when the block's `indicationAvailable` is true. That flag is its
 * own axis: minting the letter is a later step than granting the program, so an
 * approved account can carry no PDF for a moment, and reading `status ===
 * "approved"` as "there is a letter" is how a broker gets a 404 (PMS-S306's own
 * guidance, and BRK-S133's lesson).
 */
export function getAccountIndicationPdf(
  fetchBlob: AuthedBlobRequester,
  id: string,
): Promise<FileResponse> {
  return fetchBlob(`${BASE}/${encodeURIComponent(id)}/indication.pdf`);
}

/**
 * One document on the account (PMS-S304's `account_document`).
 *
 * DELIBERATELY NOT AN INTAKE DOCUMENT. Those are broker uploads hung off an
 * `intake_batch`, with classification and extraction state and an advisory
 * account id (BRK-S038 consumes them). These are system-produced or signed
 * records that belong to one account BY DEFINITION, and they are **versioned per
 * (account, type)** — so a re-mint APPENDS rather than replacing, and the
 * history is the record.
 *
 * `superseded` is therefore a normal state and not a fault: it says a newer
 * version of the same document type exists. It is marked rather than hidden,
 * because a letter Signal issued is a record of what was issued — a re-approval
 * on new terms does not un-issue the old one.
 */
export interface AccountDocument {
  id: string;
  /** The document TYPE, as PMS names it (e.g. `program_indication`). An
   *  open vocabulary on purpose: PMS adds types on its own cadence, so this is
   *  a string the client LABELS rather than a union it branches on — an
   *  unrecognised type must list and download, never disappear. */
  type: string;
  /** 1-based, per (account, type). Version numbers survive a soft delete, so a
   *  re-mint cannot reuse one and a gap is meaningful. */
  version: number;
  filename: string;
  createdAt: string;
  /** Whether a newer version of this type exists. */
  superseded: boolean;
  /**
   * The `SB-#####` of the bond order this document came in with, or null for
   * one that belongs to the account on its own (BRK-S146).
   *
   * ⚠️ NOT PUBLISHED YET. The story brief named it, and PMS-S332/S334/S335 all
   * leave this row at six fields — so it is read OPTIONAL here, which renders
   * nothing at all until it arrives, and PMS-S340 is filed for it. Reading it
   * early costs nothing and means the column lights up on the day PMS ships;
   * `test/lib/suretyProgramContract.test.ts` fails the moment it is published,
   * so this comment cannot outlive its own truth.
   *
   * NULLABLE AND OPTIONAL, which are two different absences and both real. Null
   * is "no order occasioned this" — the Program Indication letter belongs to
   * the insured's standing program, not to whichever order happened to trigger
   * the evaluation. The key being MISSING is a PMS that predates PMS-S335, and
   * the row must still render: reading an absent key as "no order" would be
   * guessing, so the column is simply not shown for it.
   *
   * The NUMBER rather than the id, deliberately: it is the string a broker can
   * read back over the phone, and it is what the bond list shows.
   */
  bondSubmissionNumber?: string | null;
}

/** GET /api/broker/accounts/{id}/documents — the account's documents, own-scoped. */
export function listAccountDocuments(
  request: AuthedRequester,
  id: string,
): Promise<{ documents: AccountDocument[] }> {
  return request<{ documents: AccountDocument[] }>(
    `${BASE}/${encodeURIComponent(id)}/documents`,
  );
}

/**
 * GET /api/broker/accounts/{id}/documents/{docId}/download — one document's
 * BYTES.
 *
 * Streams through the app tier rather than redirecting to a presigned URL, which
 * is PMS's own decision and worth knowing here: the session IS the
 * authorization, and a presigned URL would outlive the request that minted it
 * (PMS-S304). So this is another blob fetch, and the server names the file.
 */
export function downloadAccountDocument(
  fetchBlob: AuthedBlobRequester,
  id: string,
  documentId: string,
): Promise<FileResponse> {
  return fetchBlob(
    `${BASE}/${encodeURIComponent(id)}/documents/${encodeURIComponent(documentId)}/download`,
  );
}

// ─── The insured's ISSUED bonds (BRK-S175, consuming PMS-S463) ───────────────

/**
 * One bond Signal has actually issued for this insured.
 *
 * DELIBERATELY NOT A BOND ORDER. A `BondSubmissionListItem` is a REQUEST — it
 * exists from draft start, it can be half-filled, referred or abandoned, and its
 * `SB-#####` is a number for the conversation about getting a bond. This is the
 * bond ITSELF: one row per COMPLETED issuance, with the number an obligee reads
 * off the executed form. The two lists sit on adjacent tabs of the same profile
 * and answer different questions, which is why they are two reads and not one
 * with a status filter.
 *
 * NOTHING ABOUT MONEY OR APPETITE. No premium, no rate, no rating input, no
 * envelope internals (PMS-S463's own boundary). The surface stays inside what a
 * broker may be told, the same rule the standing program block is pinned to
 * (`test/lib/preapprovalDiscretion.test.ts` sweeps this file too).
 *
 * `bondId` is the ISSUANCE id and is stable: riders and invoices are meant to
 * hang off a row here later, so it is the handle those will address.
 */
export interface AccountBond {
  /** The issuance id — stable, and what later riders/invoices will address.
   *  The one field on this row that is never null. */
  bondId: string;
  /**
   * WHICH bond this is, as the discovery CATALOGUE'S SUBPRODUCT KEY — the value
   * the form declares (`bid_bond`), never a label.
   *
   * ⚠️ PMS's own note, and the reason this is not rendered raw: "the catalogue
   * lives in discovery and PMS is not the authority on what it holds, so label
   * it from `GET /api/broker/bond/catalog`." Printing the key puts `bid_bond`
   * on a page a broker hands an obligee. `bondTypeLabel` is the one place that
   * resolution happens.
   */
  bondType: string;
  /** The number on the executed form — what an obligee quotes back. NULL for an
   *  issuance whose form maps none, which is a real state rather than a gap. */
  bondNumber: string | null;
  /** When the bond was issued, ISO. Displayed `m/d/yyyy` (D-10 rule 2). */
  bondDate: string | null;
  /**
   * When cover starts, ISO. Distinct from `bondDate` — a bond issued today can
   * take effect next month, and an obligee cares about both.
   *
   * NULLABLE like the other two dates, and for PMS's own reason: they are "the
   * three facts the underwriter keyed at prepare time", and a form that maps
   * none of them still has a bond number. A placeholder would state a fact
   * nobody stated.
   */
  effectiveDate: string | null;
  /** The obligee's own form, by title. Never null — a row that cannot say which
   *  piece of paper a bond went out on is a uuid. */
  formTitle: string;
  obligeeName: string | null;
  /** The `SB-#####` of the order this bond came out of — the string a broker
   *  reads back over the phone. */
  orderNumber: string | null;
  /**
   * The source order's POINTER id, so the row can LINK to the submission
   * profile — the same id `/api/broker/bond/submissions/{id}` takes.
   *
   * NULLABLE though every issuance has an order today: PMS published it that
   * way so a later source of issued bonds (one migrated in, a rider against no
   * new order) is not a breaking change. A row with no id renders its number as
   * plain text rather than a dead link.
   */
  orderId: string | null;
  /**
   * The executed bond PDF's `account_document`, for the existing download route
   * — "one download door for every broker-visible document" (BRK-S146).
   *
   * NULL when the envelope completed and the filing has not landed: **the bond
   * is real and the PDF is not there yet.** So the row still lists and the
   * Download is withheld, rather than offering a button that can only 404.
   */
  documentId: string | null;
}

/**
 * GET /api/broker/accounts/{id}/bonds — the insured's issued bonds, own-scoped.
 *
 * VALIDATES ITS ARRAY rather than coercing a drifted body to `[]` (BRK-S146's
 * lesson, found by an e2e whose catch-all answered `{}`): an empty list is the
 * honest "no bonds yet" state and must never be what a shape this reader did not
 * recognise looks like. A broker told "no bonds" about an insured who holds
 * three is worse than a handled error.
 */
export async function listAccountBonds(
  request: AuthedRequester,
  id: string,
): Promise<{ bonds: AccountBond[] }> {
  const body = await request<{ bonds?: unknown }>(
    `${BASE}/${encodeURIComponent(id)}/bonds`,
  );
  if (!Array.isArray(body?.bonds)) {
    // An `ApiError`, not a bare `Error`: `messageFor` relays only the former's
    // message verbatim, so throwing anything else would replace this sentence
    // with the generic fallback — the same shape `listBondDocuments` uses.
    throw new ApiError(
      200,
      "We couldn’t read this insured’s bonds — reload the page to try again.",
      body,
    );
  }
  return { bonds: body.bonds as AccountBond[] };
}

/**
 * One ISSUED bond across the whole brokerage (BRK-S181, PMS-S479 revision 2) —
 * the account route's row plus the insured it belongs to.
 *
 * The Surety nav's **Bonds** page reads these, and it is deliberately NOT the
 * orders list with a filter: a `BondSubmissionListItem` is a request, this is
 * the bond itself (see `AccountBond`). Pinned since the re-pin onto
 * PMS-S479 — see `test/lib/brokerageBondsContract.test.ts`.
 */
export interface BrokerageBond extends AccountBond {
  /** The insured's account id — what the row links to, and whose document
   *  route the executed PDF downloads through. */
  accountId: string;
  /** The insured's legal name, for the row. */
  accountName: string;
}

/**
 * GET /api/broker/bonds — the brokerage's issued bonds, own-scoped and PAGED
 * (`limit` 1–100, `offset`), newest first.
 *
 * Validates its array, like `listAccountBonds`, for the same reason: a drifted
 * body read as "no bonds" tells a broker something false.
 */
export async function listBrokerageBonds(
  request: AuthedRequester,
  query: { limit?: number; offset?: number } = {},
): Promise<Page<BrokerageBond>> {
  const qs = new URLSearchParams();
  if (query.limit != null) qs.set("limit", String(query.limit));
  if (query.offset != null) qs.set("offset", String(query.offset));
  const s = qs.toString();
  const body = await request<Partial<Page<unknown>>>(
    `/api/broker/bonds${s ? `?${s}` : ""}`,
  );
  if (!Array.isArray(body?.items) || typeof body.total !== "number") {
    throw new ApiError(
      200,
      "We couldn’t read your brokerage’s bonds — reload the page to try again.",
      body,
    );
  }
  return {
    items: body.items as BrokerageBond[],
    total: body.total,
    limit: typeof body.limit === "number" ? body.limit : body.items.length,
    offset: typeof body.offset === "number" ? body.offset : 0,
  };
}

// ─── Reference data (PMS-S066) ────────────────────────────────────────────────

export interface NamedRef {
  id: string;
  name: string;
}

/** A broker in the caller's own brokerage, for the servicing-broker picker. */
export interface BrokerSummary {
  id: string;
  firstName: string;
  lastName: string;
  businessUnit: string | null;
  email: string;
}

export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

// ─── List / detail ─────────────────────────────────────────────────────────--

export interface AccountListQuery {
  search?: string;
  includeInactive?: boolean;
  limit?: number;
  offset?: number;
}

function listQuery(query: AccountListQuery): string {
  const qs = new URLSearchParams();
  if (query.search) qs.set("search", query.search);
  if (query.includeInactive) qs.set("includeInactive", "true");
  if (query.limit != null) qs.set("limit", String(query.limit));
  if (query.offset != null) qs.set("offset", String(query.offset));
  const s = qs.toString();
  return s ? `?${s}` : "";
}

/** GET /api/broker/accounts — the brokerage's own accounts (summary only). */
export function listAccounts(
  request: AuthedRequester,
  query: AccountListQuery = {},
): Promise<AccountsPage> {
  return request<AccountsPage>(`${BASE}${listQuery(query)}`);
}

/** GET /api/broker/accounts/{id} — the account (EIN, no SSN) + its children.
 *  404 when the account isn't the caller's brokerage's. */
export function getAccount(
  request: AuthedRequester,
  id: string,
): Promise<AccountDetail> {
  return request<AccountDetail>(`${BASE}/${id}`);
}

// ─── Create / edit (account:edit) ─────────────────────────────────────────────

/** POST /api/broker/accounts — create under the session brokerage. No de-dup:
 *  resolves to the created account (201). */
export function createAccount(
  request: AuthedRequester,
  input: AccountInput,
): Promise<AccountDetail> {
  return request<AccountDetail>(BASE, { method: "POST", body: input });
}

/** The minimal New Business body (PMS-S134): insured legal name + 2-letter
 *  state, plus the industry captured at step 1 (optional on the wire —
 *  PMS-S141 — but the intake UI always sends it). */
export interface DraftAccountInput {
  legalName: string;
  state: string;
  industryId?: string;
}

/** POST /api/broker/accounts/draft — start a minimal DRAFT account (name + state)
 *  under the session brokerage, so a New Business submission can be filed against
 *  it before the full account is known (PMS-S134). Returns the created account. */
export function createDraftAccount(
  request: AuthedRequester,
  input: DraftAccountInput,
): Promise<AccountDetail> {
  return request<AccountDetail>(`${BASE}/draft`, {
    method: "POST",
    body: input,
  });
}

/** PUT /api/broker/accounts/{id} — edit the account (brokerage forced from the
 *  session). Returns the refreshed detail. */
export function updateAccount(
  request: AuthedRequester,
  id: string,
  input: AccountInput,
): Promise<AccountDetail> {
  return request<AccountDetail>(`${BASE}/${id}`, {
    method: "PUT",
    body: input,
  });
}

// ─── Notes (account-scoped, account:edit) ─────────────────────────────────────

// A broker's "message us" about one of their own accounts (BRK-S159, pairs
// PMS-S356). Recorded as an inbound communication (the server fixes source=broker
// / direction=inbound / medium=portal) in the evidentiary Communication Log —
// durable + account-scoped, deliberately distinct from the Crisp chat widget. The
// receipt is a narrow confirmation (no internal SLA/ack columns).
export interface AccountMessageReceipt {
  id: string;
  communicationNumber: string;
  subject: string;
  occurredAt: string;
}

export function sendAccountMessage(
  request: AuthedRequester,
  id: string,
  input: { subject: string; body: string },
): Promise<AccountMessageReceipt> {
  return request<AccountMessageReceipt>(`${BASE}/${id}/messages`, {
    method: "POST",
    body: input,
  });
}

export function addNote(
  request: AuthedRequester,
  id: string,
  body: string,
): Promise<AccountNote> {
  return request<AccountNote>(`${BASE}/${id}/notes`, {
    method: "POST",
    body: { body },
  });
}

export function updateNote(
  request: AuthedRequester,
  id: string,
  noteId: string,
  body: string,
): Promise<AccountNote> {
  return request<AccountNote>(`${BASE}/${id}/notes/${noteId}`, {
    method: "PUT",
    body: { body },
  });
}

export async function deleteNote(
  request: AuthedRequester,
  id: string,
  noteId: string,
): Promise<void> {
  await request<unknown>(`${BASE}/${id}/notes/${noteId}`, { method: "DELETE" });
}

// ─── Reference lookups for the create/edit form + servicing-broker picker ─────

export function listIndustries(
  request: AuthedRequester,
  query: AccountListQuery = {},
): Promise<Page<NamedRef>> {
  return request<Page<NamedRef>>(`/api/broker/industries${listQuery(query)}`);
}

export function listConstructionManagementSystems(
  request: AuthedRequester,
  query: AccountListQuery = {},
): Promise<Page<NamedRef>> {
  return request<Page<NamedRef>>(
    `/api/broker/construction-management-systems${listQuery(query)}`,
  );
}

/**
 * GET /api/broker/codes?type=naics&search= — the NAICS typeahead's source
 * (BRK-S138).
 *
 * The SAME `/api/broker/codes` route the class-code search uses, addressed by
 * `type` rather than by a route of its own, because a NAICS code is a code:
 * PMS's `code_type` reference table is what makes the vocabularies pluggable
 * (PMS-S020), and a `/naics` endpoint would be a second answer to "where do
 * codes come from".
 *
 * NOT own-scoped and NOT appetite-scoped, which is the difference from
 * `searchSubmissionCodes` worth naming: this is a reference lookup over a public
 * federal classification, so there is no submission to scope it to and no
 * empty-with-reason to explain — an empty page here is a plain search miss.
 */
export function searchNaicsCodes(
  request: AuthedRequester,
  search: string,
): Promise<{ items: NaicsCode[] }> {
  // `type` and `search`, and NO `limit`: those are the only two parameters the
  // contract declares (reconciled against the as-built PMS-S314 spec), and
  // sending a third would be this portal relying on a knob the server has not
  // promised to honour. The page size is upstream's to choose.
  const qs = new URLSearchParams({ type: "naics", search });
  return request<{ items: NaicsCode[] }>(`/api/broker/codes?${qs.toString()}`);
}

/** GET /api/broker/brokers — the caller's own brokerage roster (PMS-S066). */
export function listBrokerageBrokers(
  request: AuthedRequester,
  query: AccountListQuery = {},
): Promise<Page<BrokerSummary>> {
  return request<Page<BrokerSummary>>(`/api/broker/brokers${listQuery(query)}`);
}
